import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories, tags, siteConfig } from '@/mocks/siteData';
import { JsonLd, getBreadcrumbSchema } from '@/components/base/JsonLd';

function formatReadCount(count: number): string {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万`;
  }
  return count.toString();
}

function getArticleContent(article: (typeof articles)[number], categoryName?: string): string {
  if (!article.content.includes('内容加载中')) {
    return article.content;
  }

  return `<p>${article.excerpt}</p>
    <p>五一吃瓜网前线整理显示，这条${categoryName || '热点'}内容在发布后迅速引发讨论，读者关注点主要集中在事件经过、相关回应以及后续影响。</p>
    <h2>事件脉络</h2>
    <p>从目前公开信息看，事件最早在社交平台发酵，随后多个讨论区开始集中转发。由于当事方暂未给出完整说明，围绕细节真实性、时间线和责任归属的争议仍在持续。</p>
    <h2>网友关注</h2>
    <p>不少网友认为，类似事件不能只停留在情绪化围观，更需要结合证据链、当事方回应和后续处理结果综合判断。也有读者提醒，吃瓜归吃瓜，转发和评论时仍应避免未经核实的信息扩散。</p>
    <p>本站将继续跟进这条爆料的最新进展，并在出现可靠补充信息后更新相关内容。</p>`;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <div className="max-w-lg mx-auto px-4">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-primary-600 text-3xl"></i>
            </div>
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-8">这个瓜可能被吃完了，或者链接不对哦~</p>
            <Link to="/" className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer text-sm">
              <i className="ri-arrow-left-line"></i>返回首页继续吃瓜
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const articleTags = tags.filter((t) => article.tags.includes(t.slug));
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://${siteConfig.domain}/article/${article.slug}`,
    },
    keywords: articleTags.map((t) => t.name).join(', '),
    about: category ? { '@type': 'Thing', name: category.name } : undefined,
  };

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: '首页', url: `http://${siteConfig.domain}` },
    ...(category ? [{ name: category.name, url: `http://${siteConfig.domain}/category/${category.slug}` }] : []),
    { name: article.title, url: `http://${siteConfig.domain}/article/${article.slug}` },
  ]);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />

      <main>
        <section className="relative w-full min-h-[500px] md:min-h-[600px] flex items-center">
          <div className="absolute inset-0">
            <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 pt-20">
            <div className="max-w-4xl mx-auto text-center">
              {category && (
                <Link to={`/category/${category.slug}`} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-6 hover:bg-white/25 transition-colors duration-200 cursor-pointer">
                  <i className={`${category.icon} text-white/90 text-sm`}></i>
                  <span className="text-xs font-medium text-white/90">{category.name}</span>
                </Link>
              )}
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight mb-4 text-balance">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">{article.excerpt}</p>
              <div className="flex flex-wrap items-center justify-center gap-4 mt-6 text-sm text-white/60">
                <span className="flex items-center gap-1.5"><i className="ri-user-line"></i>{article.author}</span>
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>{article.publishDate}</span>
                <span className="flex items-center gap-1.5"><i className="ri-eye-line"></i>{formatReadCount(article.readCount)} 阅读</span>
                <span className="flex items-center gap-1.5"><i className="ri-chat-3-line"></i>{formatReadCount(article.commentCount)} 评论</span>
              </div>
            </div>
          </div>
        </section>

        <article className="py-12 md:py-16">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            <div className="flex flex-wrap gap-2 mb-8">
              {articleTags.map((tag) => (
                <Link key={tag.slug} to={`/category/${article.category}`} className="whitespace-nowrap px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors duration-200">
                  #{tag.name}
                </Link>
              ))}
            </div>
            <div className="prose prose-lg max-w-none font-body text-foreground-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: getArticleContent(article, category?.name) }} />
            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex items-center gap-4">
                <span className="text-sm text-foreground-500">分享到：</span>
                <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors duration-200 cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line text-foreground-600"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors duration-200 cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line text-foreground-600"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors duration-200 cursor-pointer" aria-label="复制链接">
                  <i className="ri-share-forward-line text-foreground-600"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1.5 h-5 rounded-full bg-primary-500"></div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">相关爆料</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link key={ra.id} to={`/article/${ra.slug}`} className="group cursor-pointer">
                    <div className="rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300">
                      <div className="aspect-[16/10] overflow-hidden">
                        <img src={ra.coverImage} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-sm font-bold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">{ra.title}</h3>
                        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                          <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatReadCount(ra.readCount)}</span>
                          <span>{ra.publishDate}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-6">
                  <span className="w-2 h-2 rounded-full bg-foreground-900"></span>
                  <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">网友神评</span>
                </div>
                <blockquote className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-snug mb-6 max-w-lg">
                  <span className="text-primary-500 text-4xl md:text-5xl">"</span>
                  这是我今年吃过最劲爆的瓜！五一吃瓜网永远不让人失望，每天蹲点等更新。
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary-200 overflow-hidden">
                    <img src="https://readdy.ai/api/search-image?query=Young%20Chinese%20person%20portrait%2C%20casual%20style%2C%20warm%20lighting%2C%20happy%20expression%2C%20clean%20cream%20background%2C%20professional%20headshot&width=100&height=100&seq=comment-avatar-01&orientation=squarish" alt="吃瓜群众" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">吃瓜老司机</p>
                    <p className="text-xs text-accent-600 flex items-center gap-1"><i className="ri-verified-badge-line"></i>资深吃瓜群众</p>
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <div className="aspect-[4/5] max-w-md mx-auto rounded-2xl overflow-hidden bg-background-100">
                  <img src="https://readdy.ai/api/search-image?query=Artistic%20black%20and%20white%20portrait%20of%20young%20Chinese%20person%20looking%20up%2C%20side%20profile%2C%20dramatic%20lighting%2C%20editorial%20photography%2C%20clean%20white%20background%2C%20contemplative%20expression%2C%20fine%20art%20style&width=600&height=750&seq=comment-portrait-01&orientation=portrait" alt="吃瓜群众" className="w-full h-full object-cover object-top" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
